'use client';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { Path, UseFormReturn } from 'react-hook-form';
import { format } from 'date-fns';

import Button from '../Button';
import Select, { SelectOption } from '../Select';
import DatePicker from '../DatePicker';
import QuantitieInput, { QuantityOption } from '../QuantitieInput';

import { IDatePickerDictonary } from '@/dictionaries/components/datePicker/types';
import { FormValues } from './schema';
import { cn } from '@/lib/utils';
import { IQuantitiesInputDictonary } from '@/dictionaries/components/quantitiesInput/types';
import { IVillagesHeaderDictonary } from '@/dictionaries/components/villagesHeader/types';
import {
  pugliaMap,
  sicilyMap,
  sicilyZonesValuesMap,
  villagesMap,
} from '@/utils/maps';
import useFilterContext from '@/hooks/useFilterContext';

export type VillagesHeaderDicts = {
  datePickerDict: IDatePickerDictonary;
  destinationsDict: SelectOption[];
  quantitiesInputDict: IQuantitiesInputDictonary;
  contentDict: IVillagesHeaderDictonary;
};

type Props = {
  dicts: VillagesHeaderDicts;
  className?: string;
  lang: string;
  form: UseFormReturn<FormValues, any, undefined>;
  isHome?: boolean;
} & PropsWithChildren;

declare const window: Window & { dataLayer: Record<string, unknown>[] };

export default function VillagesHeaderFilter({
  dicts: { quantitiesInputDict, contentDict, datePickerDict, destinationsDict },
  className,
  children,
  lang,
  form,
  isHome,
}: Props) {
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const {
    villageFilter,
    setVillageFilter,
    handleVillageFilter,
    setQuantities,
    setSelectedDates,
  } = useFilterContext();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();

  const allVillasSlugs: Record<string, string> = {
    en: 'all-villas',
    it: 'tutte-le-ville',
    de: 'alle-villen',
    fr: 'toutes-les-villas',
  };

  const pathnameSplit = pathname.split('/');
  const englishLang = lang === 'en';
  const currentRegion = englishLang ? pathnameSplit[1] : pathnameSplit[2];
  const params = new URLSearchParams(Array.from(searchParams.entries()));
  const locationParam = params.get('location');
  const atAllVillasPage = currentRegion === allVillasSlugs[lang];

  const quantitiesInputOptions: QuantityOption[] = [
    { label: quantitiesInputDict.adultsLabel, name: 'adults', value: 0 },
    {
      label: quantitiesInputDict.childrenLabel,
      name: 'children',
      value: 0,
    },
    { label: quantitiesInputDict.infantsLabel, name: 'infants', value: 0 },
  ];

  const { watch, setValue, register } = form;

  const filteredOptions = useMemo(() => {
    const sicilyLangMap = ['sicily', 'sizilien', 'sicilia', 'sicile'];
    let atSicily = sicilyLangMap.includes(currentRegion);

    const options = destinationsDict
      .filter(({ value }) => value !== 'all')
      .filter(({ value }) => {
        const numberValue = parseInt(value);
        const sicilyOptions = numberValue <= 4;
        const pugliaOptions = numberValue > 4 && numberValue !== 5;
        if (isHome || atAllVillasPage) {
          return true;
        }
        if (atSicily) {
          return sicilyOptions;
        } else {
          return pugliaOptions;
        }
      });

    if (!isHome) {
      options.unshift(destinationsDict[0]);
    }

    return options;
  }, [currentRegion, destinationsDict, isHome]);

  function setQuantitiesAndCalculateSleeps(params: URLSearchParams) {
    const { quantities } = watch();
    const sleeps = quantities.adults + quantities.children;

    if (params.has('adults')) {
      if (quantities.adults == 0) {
        params.delete('adults');
      }
    }
    if (params.has('children')) {
      if (quantities.children == 0) {
        params.delete('children');
      }
    }
    if (params.has('infants')) {
      if (quantities.infants == 0) {
        params.delete('infants');
      }
    }

    if (quantities.adults) {
      params.set('adults', quantities.adults.toString());
    }

    if (quantities.children) {
      params.set('children', quantities.children.toString());
    }
    if (quantities.infants) {
      params.set('infants', quantities.infants.toString());
    }

    const toSaveQuantites = {
      ...quantities,
    };

    setQuantities(toSaveQuantites);

    return sleeps;
  }

  function getFilters() {
    const { dates, destination } = watch();
    const params = new URLSearchParams(Array.from(searchParams.entries()));

    const sleeps = setQuantitiesAndCalculateSleeps(params);

    if (sleeps > 0) {
      params.set('sleeps', sleeps.toString());
    }

    if (sleeps == 0) {
      params.delete('sleeps');
    }

    if (dates.start && dates.end) {
      const startDate = format(dates.start, 'yyyy-MM-dd');
      const endDate = format(dates.end, 'yyyy-MM-dd');
      params.set('startDate', startDate);
      params.set('endDate', endDate);
      setSelectedDates({ ...dates });
    }

    if (!dates.start && !dates.end) {
      params.delete('startDate');
      params.delete('endDate');
    }

    if (destination.value !== 'all' && !isHome) {
      params.set('location', destination.value);
    }

    if (destination.value == 'all') {
      params.delete('location');

      handleVillageFilter(params);
    }

    return params;
  }

  function navigate() {
    const filtersObj = getFilters();
    filtersObj.delete('q');
    filtersObj.delete('page');

    const filters = filtersObj.toString();

    const { destination } = watch();
    const destinationValue = Number(destination.value);
    let region = isHome
      ? destinationValue >= 1 && destinationValue < 5
        ? sicilyMap[lang]
        : pugliaMap[lang]
      : currentRegion;

    const path = `${lang === 'en' ? `${isHome ? '' : '/'}` : `/${lang}/`}${region}${!atAllVillasPage ? `/${villagesMap[lang]}` : ''}?${filters}`;

    router.push(path);
  }

  function handleOpenDatePicker(open: boolean) {
    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      event: 'eventBookingFlow',
      eventCategory: 'booking',
      eventAction: 'booking_check_in',
      eventLabel: 'VillaListing',
      language: lang,
    });

    setModalIsOpen(open);
  }

  useEffect(() => {
    const probableRegionLabel = pathnameSplit[pathnameSplit.length - 1];
    const locationFromRegionLabel = sicilyZonesValuesMap[probableRegionLabel];

    if (locationFromRegionLabel) {
      const option = filteredOptions.find(
        (option) => option.value === locationFromRegionLabel,
      );
      if (option) {
        setValue('destination', option);
      }
    }

    if (locationParam) {
      const option = filteredOptions.find(
        (option) => option.value === locationParam,
      );
      if (option) {
        setValue('destination', option);
      }
    }
    if (villageFilter) {
      setVillageFilter({});
    }
    // eslint-disable-next-line
  }, []);

  return (
    <div className={cn('w-full pt-8 desktop:py-4', className)}>
      {isHome ? null : (
        <div className="desktop:hidden flex flex-col items-center mt-[-30px] w-full">
          <h4 className="mb-0 text-xl leading-[1.25] mt-0 font-title">
            {contentDict.filters}
          </h4>
          <hr className="mt-2 block clear-both w-full max-w-default h-0 my-[1.25rem] mx-auto border-b border-gray-200" />
        </div>
      )}
      <div
        className={cn(
          'flex flex-1 flex-col gap-4 desktop:gap-0 desktop:flex-row desktop:items-end desktop:w-[780px] [@media(min-width:1300px)]:w-[850px] [@media(min-width:1400px)]:w-[950px] max-w-[1200px] mx-auto',
          isHome
            ? 'backdrop-blur-md p-8 desktop:pt-6 w-full text-gray-850'
            : 'mx-0',
        )}>
        <div className="w-full desktop:mr-4 desktop:w-[210px] [@media(min-width:1400px)]:w-[350px]">
          <Select
            label={contentDict.destination}
            id="destination"
            onChange={(value) => setValue('destination', value)}
            options={filteredOptions}
            selectedValue={watch('destination')}
            labelClassName={isHome ? 'text-white' : ''}
          />
        </div>
        <div className="flex flex-col-reverse flex-1 gap-4 desktop:gap-0 desktop:flex-row">
          <DatePicker.Root
            open={modalIsOpen}
            onOpenChange={handleOpenDatePicker}>
            <div className="w-full flex-1 desktop:mr-4 desktop:w-[210px] [@media(min-width:1400px)]:w-[250px]">
              <DatePicker.Trigger
                dict={datePickerDict}
                confirmedDates={watch('dates')}
                labelClassName={isHome ? 'text-white' : ''}
              />
            </div>
            <DatePicker.Content
              showSummary
              confirmedDates={watch('dates')}
              confirmDates={(value) => setValue('dates', value)}
              onOpenChange={setModalIsOpen}
              dict={datePickerDict}
            />
          </DatePicker.Root>
          <div className="w-full flex-1 desktop:mr-4 desktop:w-[195px]">
            <QuantitieInput
              dict={quantitiesInputDict}
              label={contentDict.guests}
              id="quantities"
              register={register}
              onChange={({ value, name }) => {
                setValue(`quantities.${name}` as Path<FormValues>, value);
              }}
              options={quantitiesInputOptions}
              addedQuantities={watch('quantities')}
              labelClassName={isHome ? 'text-white' : ''}
            />
          </div>
        </div>

        <Button
          onClick={navigate}
          className="justify-center items-center w-auto h-[44px] relative whitespace-nowrap flex mt-2">
          {contentDict.searchVillas}
        </Button>
      </div>
      {children}
    </div>
  );
}
